import { Store } from 'antd/lib/form/interface';
import {
  ChaperoneTransportationSequence,
  FlightSequence,
  GroundTransportationSequence,
  MeetupSequence,
  SequenceTypesIds,
  TransporterSequence,
  TravelPlan,
  ChaperoneCompany,
  Nullable,
  TransporterCompany,
  Airline,
  TravelSequence,
  SequencePayload,
} from 'types';
import moment from 'moment';
import { log } from 'utilities/log/log';
import { notify } from 'utilities/notifications/notify';
import { apiSequence } from 'utilities/http/api/leash/apiSequence';
import { LeashContent } from 'leash/utilities/localStorage/leashContentStorage';
import date from 'utilities/dates/date';

export const populateFormOnLoad = (
  sequence: TravelSequence,
  sequenceTypeId: SequenceTypesIds,
  index: number,
) => {
  switch (sequenceTypeId) {
    case SequenceTypesIds.GroundTransportationSequence: {
      const groundTransportationSequence = sequence as GroundTransportationSequence;
      const linkedCompany = getGroundTransportationCompany(
        groundTransportationSequence.groundTransportationCompanyId,
      );
      return {
        [`companyGround${index}`]: groundTransportationSequence.groundTransportationCompanyId,
        [`companyPhone${index}`]: linkedCompany?.phone,
        [`companyNotes${index}`]: linkedCompany?.additionalInformation,
        [`driverName${index}`]: groundTransportationSequence.driverName,
        [`departureDateGround${index}`]: moment(
          groundTransportationSequence.departureTime,
        ),
        [`departureTimeGround${index}`]: moment(
          groundTransportationSequence.departureTime,
        ),
        [`arrivalDateGround${index}`]: moment(groundTransportationSequence.arrivalTime),
        [`arrivalTimeGround${index}`]: moment(groundTransportationSequence.arrivalTime),
      };
    }
    case SequenceTypesIds.FlightSequence: {
      const flightSequence = sequence as FlightSequence;
      const airline = getAirline(flightSequence.airlineId);
      return {
        [`AirlineIdFlight${index}`]: flightSequence.airlineId,
        [`airlinePhone${index}`]: airline?.phone,
        [`airlineNotes${index}`]: airline?.additionalInformation,
        [`flightNumber${index}`]: flightSequence.flightNumber,
        [`waybill${index}`]: flightSequence.flightAirWaybill,
        [`confirmationNumber${index}`]: flightSequence.flightConfirmation,
        [`departureAirportFlight${index}`]: flightSequence.departureAirportId,
        [`departureDateFlightFlight${index}`]: moment(flightSequence.departureTime),
        [`departureTimeFlightFlight${index}`]: moment(flightSequence.departureTime),
        [`arrivalAirportFlight${index}`]: flightSequence.arrivalAirportId,
        [`arrivalDateFlightFlight${index}`]: moment(flightSequence.arrivalTime),
        [`arrivalTimeFlightFlight${index}`]: moment(flightSequence.arrivalTime),
      };
    }
    case SequenceTypesIds.TransporterSequence: {
      const transporterSequence = sequence as TransporterSequence;
      return {
        [`TransporterCompany${index}`]: transporterSequence.transporterCompanyId,
        [`TransporterPhone${index}`]: transporterSequence.additionalPhone,
        [`departureDateTransporter${index}`]: moment(transporterSequence.departureTime),
        [`departureTimeTransporter${index}`]: moment(transporterSequence.departureTime),
        [`arrivalDateTransporter${index}`]: moment(transporterSequence.arrivalTime),
        [`arrivalTimeTransporter${index}`]: moment(transporterSequence.arrivalTime),
      };
    }
    case SequenceTypesIds.MeetupSequence: {
      const meetupSequence = sequence as MeetupSequence;
      return {
        [`meetupLocation${index}`]: meetupSequence.address,
        [`meetupLocation2${index}`]: meetupSequence.address2,
        [`city${index}`]: meetupSequence.city,
        [`state${index}`]: meetupSequence.stateId,
        [`meetupLocationName${index}`]: meetupSequence.pickupLocationName,
        [`postalCode${index}`]: meetupSequence.postalCode,
        [`exchangeNumbers${index}`]: meetupSequence.exchangedNumbers === 0 ? 'NO' : 'YES',
        [`meetupDate${index}`]: moment(meetupSequence.pickupTime),
        [`meetupTimeOfDay${index}`]: moment(meetupSequence.pickupTime),
      };
    }
    case SequenceTypesIds.PuppyChaperoneFlightSequence:
    default: {
      const puppyChaperoneFlightSequence = sequence as ChaperoneTransportationSequence;
      return {
        [`chaperoneName${index}`]: puppyChaperoneFlightSequence.puppyChaperoneCompanyId,
        [`chaperonePhone${index}`]: getChaperonePhone(
          puppyChaperoneFlightSequence.puppyChaperoneCompanyId,
        ),
        [`additionalPhone${index}`]: puppyChaperoneFlightSequence.additionalPhone,
        [`AirlineIdChaperone${index}`]: puppyChaperoneFlightSequence.flightSequence
          ?.airlineId,
        [`flightNumberChaperone${index}`]: puppyChaperoneFlightSequence.flightSequence
          ?.flightNumber,
        [`recordLocator${index}`]: puppyChaperoneFlightSequence.flightSequence
          ?.flightAirWaybill,
        [`departureAirportChaperone${index}`]: puppyChaperoneFlightSequence.flightSequence
          ?.departureAirportId,
        [`departureDateFlightChaperone${index}`]: puppyChaperoneFlightSequence
          .flightSequence?.departureTime
          ? moment(puppyChaperoneFlightSequence.flightSequence?.departureTime)
          : '',
        [`departureTimeFlightChaperone${index}`]: puppyChaperoneFlightSequence
          .flightSequence?.departureTime
          ? moment(puppyChaperoneFlightSequence.flightSequence?.departureTime)
          : '',
        [`arrivalAirportChaperone${index}`]: puppyChaperoneFlightSequence.flightSequence
          ?.arrivalAirportId,
        [`arrivalDateFlightChaperone${index}`]: puppyChaperoneFlightSequence
          .flightSequence?.arrivalTime
          ? moment(puppyChaperoneFlightSequence.flightSequence?.arrivalTime)
          : '',
        [`arrivalTimeFlightChaperone${index}`]: puppyChaperoneFlightSequence
          .flightSequence?.arrivalTime
          ? moment(puppyChaperoneFlightSequence.flightSequence?.arrivalTime)
          : '',
      };
    }
  }
};

export const buildSequencePayload = (
  e: Store,
  sequenceTypeId: SequenceTypesIds,
  index: number,
): SequencePayload | undefined => {
  switch (sequenceTypeId) {
    case SequenceTypesIds.GroundTransportationSequence: {
      const groundTransportationSequence: GroundTransportationSequence = {
        groundTransportationCompanyId: e[`companyGround${index}`],
        driverName: e[`driverName${index}`],
        ...getDateAndTimeForm(e, 'Ground', index),
        travelSequenceTypeSlug: null,
      };
      return { groundTransportation: groundTransportationSequence };
    }
    case SequenceTypesIds.FlightSequence: {
      const flightSequence: FlightSequence = {
        departureAirportId: e[`departureAirportFlight${index}`],
        arrivalAirportId: e[`arrivalAirportFlight${index}`],
        airlineId: e[`AirlineIdFlight${index}`],
        flightNumber: e[`flightNumber${index}`],
        flightAirWaybill: e[`waybill${index}`],
        flightConfirmation: e[`confirmationNumber${index}`],
        ...getDateAndTimeForm(e, 'Flight', index, true),
        travelSequenceTypeSlug: null,
      };
      return { flight: flightSequence };
    }
    case SequenceTypesIds.TransporterSequence: {
      const transporterSequence: TransporterSequence = {
        transporterCompanyId: e[`TransporterCompany${index}`],
        additionalPhone: e[`TransporterPhone${index}`],
        ...getDateAndTimeForm(e, 'Transporter', index),
        travelSequenceTypeSlug: null,
      };
      return { transporter: transporterSequence };
    }
    case SequenceTypesIds.MeetupSequence: {
      const meetupSequence: MeetupSequence = {
        address: e[`meetupLocation${index}`],
        address2: e[`meetupLocation2${index}`],
        postalCode: e[`postalCode${index}`],
        city: e[`city${index}`],
        stateId: e[`state${index}`],
        pickupLocationName: e[`meetupLocationName${index}`],
        exchangedNumbers: e[`exchangeNumbers${index}`] === 'YES' ? true : false,
        departureTime: e[`meetupDate${index}`],
        pickupTime: `${date.toISO(e[`meetupDate${index}`])} ${date.getTime24(
          e[`meetupTimeOfDay${index}`],
        )}`,
      };
      return { meetup: meetupSequence };
    }
    case SequenceTypesIds.PuppyChaperoneFlightSequence:
    default: {
      const puppyChaperoneFlightSequence: ChaperoneTransportationSequence = {
        puppyChaperoneCompanyId: e[`chaperoneName${index}`],
        additionalPhone: e[`additionalPhone${index}`] ?? null,
        flightSequence: {
          departureAirportId: e[`departureAirportChaperone${index}`],
          arrivalAirportId: e[`arrivalAirportChaperone${index}`],
          airlineId: e[`AirlineIdChaperone${index}`],
          flightNumber: e[`flightNumberChaperone${index}`],
          flightAirWaybill: e[`recordLocator${index}`],
          flightConfirmation: e[`recordLocator${index}`],
          ...getDateAndTimeForm(e, 'Chaperone', index, true),
          travelSequenceTypeSlug: null,
          airlinePhone: e[`additionalPhone${index}`],
          airlineAdditionalInformation: '',
        },
        travelSequenceTypeSlug: null,
      };
      return { puppyChaperoneFlightSequence: puppyChaperoneFlightSequence };
    }
  }
};

export const onSaveTravelSequence = async (
  e: Store,
  sequenceType: SequenceTypesIds,
  index: number,
  travelPlan: TravelPlan,
  item: TravelSequence,
) => {
  const travelSequencePayload = buildSequencePayload(e, sequenceType, index);
  const { travelPackage } = travelPlan;
  const { travelSequenceId, detailSequenceId, isNew } = item;
  try {
    let response;
    if (travelSequenceId && !isNew) {
      response = await apiSequence.updateSequence(
        travelPackage.id,
        sequenceType,
        travelSequencePayload,
        detailSequenceId as number,
      );
    } else {
      response = await apiSequence.createSequence(
        travelPackage.id,
        sequenceType,
        travelSequencePayload,
      );
    }

    if (!response?.isOk) {
      notify.error('Error saving the travel sequence');
    } else {
      notify.success('Travel sequence saved');
    }
  } catch (e) {
    log.debug(e);
    notify.error('Error saving the travel sequence');
  }
};

export const onReorderOrDeleteTravelSequence = async (
  travelSequenceId: number,
  travelPlan: TravelPlan,
  order?: number,
  isDelete = false,
) => {
  const { travelPackage } = travelPlan;
  try {
    if (isDelete) {
      await deleteTravelSequence(travelPackage.id, travelSequenceId);
    } else {
      await reorderTravelSequence(travelPackage.id, travelSequenceId, order as number);
    }
  } catch (e) {
    log.debug(e);
    notify.error('Error updating the travel sequence');
  }
};

export const removeSequences = async (travelPackageId: number, sequenceIds: number[]) => {
  for (const sequenceId of sequenceIds) {
    await deleteTravelSequence(travelPackageId, sequenceId);
  }
};

export const getChaperonePhone = (chaperoneCompanyId: Nullable<number>): string => {
  if (!LeashContent?.puppyChaperoneCompanies || !chaperoneCompanyId) return '';

  return LeashContent?.puppyChaperoneCompanies.find(
    (chaperoneCompany: ChaperoneCompany) => chaperoneCompany.id === chaperoneCompanyId,
  )?.phone;
};

export const getTransporterPhone = (transporterCompanyId: Nullable<number>): string => {
  if (!LeashContent?.transporterCompanies || !transporterCompanyId) return '';

  return LeashContent?.transporterCompanies.find(
    (chaperoneCompany: ChaperoneCompany) => chaperoneCompany.id === transporterCompanyId,
  )?.phone;
};

export const getGroundTransportationCompany = (
  groundTransportationCompanyId: Nullable<number>,
): TransporterCompany | null => {
  if (!LeashContent?.groundTransportationCompanies || !groundTransportationCompanyId)
    return null;

  return LeashContent?.groundTransportationCompanies.find(
    (groundTransportationCompany: TransporterCompany) =>
      groundTransportationCompany.id === groundTransportationCompanyId,
  );
};

export const getAirline = (airlineId: Nullable<number>): Airline | null => {
  if (!LeashContent?.airlines || !airlineId) return null;

  return LeashContent?.airlines.find((airline: Airline) => airline.id === airlineId);
};

const getDateAndTimeForm = (
  e: Store,
  prefix: string,
  index: number,
  isAirportFields = false,
) => {
  const departureDate =
    e[`departureDate${isAirportFields ? 'Flight' : ''}${prefix}${index}`];
  const arrivalDate = e[`arrivalDate${isAirportFields ? 'Flight' : ''}${prefix}${index}`];
  const departureTime =
    e[`departureTime${isAirportFields ? 'Flight' : ''}${prefix}${index}`];
  const arrivalTime = e[`arrivalTime${isAirportFields ? 'Flight' : ''}${prefix}${index}`];

  if (!arrivalTime || !departureTime)
    return {
      departureTime: null,
      arrivalTime: null,
    };

  return {
    departureTime: `${date.toISO(departureDate)} ${
      departureTime ? date.getTime24(departureTime) : '00:00:00'
    }`,
    arrivalTime: `${date.toISO(arrivalDate)} ${
      departureTime ? date.getTime24(arrivalTime) : '00:00:00'
    }`,
  };
};

const deleteTravelSequence = async (
  travelPackageId: number,
  travelSequenceId: number,
) => {
  try {
    const response = await apiSequence.deleteSequence(travelPackageId, travelSequenceId);
    if (!response?.isOk) {
      notify.error('Error deleting the travel sequence');
    } else {
      notify.success('Sequence deleted');
    }
  } catch (e) {
    log.debug(e);
    notify.error('Error deleting the travel sequence');
  }
};

const reorderTravelSequence = async (
  travelPackageId: number,
  travelSequenceId: number,
  order: number,
) => {
  try {
    const response = await apiSequence.reorderSequence(
      travelPackageId,
      travelSequenceId,
      order,
    );
    if (!response?.isOk) {
      notify.error('Error reordering the travel sequence');
    } else {
      notify.success('Sequence reordered');
    }
  } catch (e) {
    log.debug(e);
    notify.error('Error reordering the travel sequence');
  }
};
